import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/massage-therapist-real.jpg";

export const massageTherapists: Persona = {
  slug: "massage-therapists",
  profession: { hu: "masszőr", en: "massage therapist" },
  professionPlural: { hu: "masszőrök", en: "massage therapists" },
  professionGenitive: { hu: "masszőrként", en: "as a massage therapist" },
  heroH1: {
    hu: "Tele naptár, kevesebb adminisztráció. Indulj 10 perc alatt.",
    en: "Full calendar, less admin. Launch in 10 minutes.",
  },
  heroSub: {
    hu: "AI weboldal, online foglalás, bérletek és visszatérő ügyfél-kampányok: egy platformon önálló masszőröknek és masszázs-stúdióknak és masszázs-stúdióknak.",
    en: "AI website, online booking, passes and win-back campaigns: one platform for solo therapists and massage studios.",
  },
  heroImage,
  heroImageAlt: "Massage therapist giving a Swedish massage",
  chatExamplePrompt: {
    hu: "Hozz létre 5 alkalmas svéd masszázs bérletet, 28 000 Ft",
    en: "Create a 5-session Swedish massage pass, 28,000 HUF",
  },
  chatExampleResponse: {
    hu: "Létrehoztam, élesítve a weboldalon. Felajánljam akcióként a hírlevélben?",
    en: "Created and live on the site. Want me to feature it as a promo in the newsletter?",
  },
  accentColor: "sky",

  defaultMode: "solo",
  teamSize: { min: 1, max: 8 },
  soloLabel: { hu: "Szóló masszőr", en: "Solo therapist" },
  teamLabel: {
    hu: "Masszázs-stúdió csapattal",
    en: "Massage studio with a team",
  },

  soloDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Az AI riportot küld: 3 új foglalás éjszaka, mind kifizetve előre.",
        en: "AI sends a report: 3 new bookings overnight, all prepaid.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "60 perc svéd masszázs. A vendég kartonján már látod, hogy a tarkó-fájdalmára jött vissza.",
        en: "60-minute Swedish massage. The client card shows they're back for the neck pain.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Új érdeklődő a naptárból foglalt egy próba sportmasszázs időpontot. Az AI azonnal visszaigazolt és elküldte az érkezési infókat.",
        en: "A new lead booked a trial sports massage from the calendar. AI confirmed immediately and sent arrival info.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Egy ügyfél bérlete lejárt. Az AI 15% kedvezménnyel értesítette este 6-kor.",
        en: "A client's pass expired. AI notified them with 15% off at 6 PM.",
      },
    },
    {
      time: "20:00",
      what: {
        hu: "Napzárás: 7 kezelés, 92 000 Ft bevétel, 2 új visszatérő ügyfél.",
        en: "End of day: 7 treatments, 92,000 HUF revenue, 2 new return clients.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "07:30",
      what: {
        hu: "Stúdió dashboard: 4 masszőr 18 időpontot ad ma. Az AI optimalizálta az időpontokat.",
        en: "Studio dashboard: 4 therapists have 18 appointments today. AI optimized the slots.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "Egy masszőr beteg. Az AI értesít 5 ügyfelet, áthelyezi őket másik kollégához.",
        en: "A therapist is sick. AI notifies 5 clients and moves them to another colleague.",
      },
    },
    {
      time: "14:00",
      what: {
        hu: "Új masszőr érkezik. Felveszed a Bespire-be, 5 percen belül publikálódik a profilja a stúdió oldalán.",
        en: "A new therapist joins. You add them to Bespire, profile is live on the studio site in 5 minutes.",
      },
    },
    {
      time: "17:00",
      what: {
        hu: "Az AI mutatja: a sportmasszázs +24%-kal népszerűbb mint múlt hónapban. Javasolja az áremelést.",
        en: "AI shows: sports massage is +24% more popular than last month. Suggests a price increase.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "A wellness-csomag akció betelik. Az AI várólistára teszi a túljelentkezőket.",
        en: "Wellness package promo fills up. AI puts overflow on the waitlist.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Bevétel masszőrönként, jutalék-elszámolás kész, számlák kiállítva.",
        en: "Revenue per therapist, commission ready, invoices issued.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: { hu: "Minden egy fiókban", en: "Everything in one account" },
      body: {
        hu: "Naptár, vendég-karton, bérlet, számla: egyetlen rendszerben, váltogatás nélkül.",
        en: "Calendar, client cards, passes, invoices: in one system, no switching.",
      },
    },
    {
      icon: "Bot",
      title: {
        hu: "Lead-űrlap + automata válasz",
        en: "Lead form + auto-response",
      },
      body: {
        hu: "Új érdeklődő kitölti a foglalási űrlapot, automatikus ajánlat-email és foglalási link megy ki: neked csak a kezelésre kell készülnöd.",
        en: "New leads fill out a booking form, an auto-quote email and booking link goes out: you just prepare for the treatment.",
      },
    },
    {
      icon: "Bell",
      title: { hu: "Automata emlékeztetők", en: "Automatic reminders" },
      body: {
        hu: "Email és SMS 24 órával előtte: drasztikusan csökken a no-show arány.",
        en: "Email and SMS 24 hours ahead: no-show rate drops drastically.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Beépített online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Built-in online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with auto-invoicing.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "Korlátlan masszőr, egy ár",
        en: "Unlimited therapists, one price",
      },
      body: {
        hu: "1 vagy 8 fő: ugyanaz a havidíj. Mindenki saját naptárral és ügyfélkörrel.",
        en: "1 or 8 people: same monthly fee. Each with their own calendar and clients.",
      },
    },
    {
      icon: "ShieldCheck",
      title: { hu: "Jogosultság-kezelés", en: "Role-based access" },
      body: {
        hu: "Tulaj, recepciós, masszőr: mindenki csak azt látja, amihez joga van.",
        en: "Owner, receptionist, therapist: everyone sees only what they're allowed to.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI elosztja a foglalásokat", en: "AI assigns bookings" },
      body: {
        hu: "Új vendég: az AI specialitás (svéd, sport, talp) alapján a megfelelő masszőrhöz osztja.",
        en: "New guest: AI assigns by specialty (Swedish, sports, reflex) to the right therapist.",
      },
    },
    {
      icon: "TrendingUp",
      title: {
        hu: "Bevétel és jutalék masszőrönként",
        en: "Revenue and commission per therapist",
      },
      body: {
        hu: "Ki mennyit dolgozott, ki hozott mennyi bevételt: automatikus elszámolás Számlázz.hu-val.",
        en: "Who worked how much, who brought in what: auto-settled via Számlázz.hu.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Calendar",
      title: { hu: "Bérletek és csomagok", en: "Passes and packages" },
      body: {
        hu: "5 alkalmas svéd, 10 alkalmas sport: automata levonás, lejárat-figyelés, hosszabbítási emlékeztető.",
        en: "5-session Swedish, 10-session sports: auto-deduction, expiry tracking, renewal reminders.",
      },
    },
    {
      icon: "Bell",
      title: { hu: "No-show védelem", en: "No-show protection" },
      body: {
        hu: "Előleg foglaláskor, 24 órás emlékeztető SMS/email/WhatsApp-en, lemondási szabályok: kevesebb üres slot.",
        en: "Deposit at booking, 24-hour SMS/email/WhatsApp reminders, cancellation rules: fewer empty slots.",
      },
    },
    {
      icon: "CreditCard",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payment",
      },
      body: {
        hu: "Bankkártya, Apple/Google Pay foglaláskor, helyszíni készpénz is: minden tranzakció egy felületen, automata számlával.",
        en: "Card, Apple/Google Pay at booking, in-person cash too: every transaction in one place, auto-invoiced.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "Ügyfél-visszahívó kampány", en: "Win-back campaigns" },
      body: {
        hu: "Ha egy vendég 6+ hete nem volt, automata email vagy WhatsApp emlékeztető kedvezménnyel: te állítod be, a rendszer kiküldi.",
        en: "If a guest hasn't been in 6+ weeks, an automatic email or WhatsApp goes out with a discount: you configure, the system sends.",
      },
    },
  ],

  objections: [
    {
      question: {
        hu: "Egyedül dolgozom, nem kell ehhez egy nagy rendszer",
        en: "I work alone: I don't need a big system",
      },
      answer: {
        hu: "Pont a szóló masszőröknek lett építve. 10 perc indulás, 10 000 Ft + ÁFA / hó. Egy hely: naptár, vendég, számla. Nem kell több eszközzel zsonglőrködni.",
        en: "Built exactly for solo therapists. 10-minute launch, 10,000 HUF + VAT / month. One place: calendar, clients, invoices. No more juggling multiple tools.",
      },
    },
    {
      question: {
        hu: "Több masszőr dolgozik a stúdiónkban: bonyolult lesz?",
        en: "Multiple therapists work in our studio: will it be complicated?",
      },
      answer: {
        hu: "Nem. Minden masszőrnek saját naptára és vendégköre van. Te tulajdonosként mindent látsz, ők csak a sajátjukat. Korlátlan masszőrt adhatsz hozzá, az ár nem változik (1 vagy 8 fő: ugyanaz a 10 000 Ft + ÁFA / hó).",
        en: "No. Each therapist has their own calendar and clients. As owner you see everything, they only see theirs. Add unlimited therapists, the price doesn't change (1 or 8 people, same 10,000 HUF + VAT / month).",
      },
    },
    {
      question: {
        hu: "Mi van, ha a vendég nem jelenik meg?",
        en: "What if a client doesn't show up?",
      },
      answer: {
        hu: "Beállíthatsz előleg-fizetést a foglaláskor, vagy lemondási szabályt (pl. 12 órán belül levonja a bérletből). Az AI 24 órával előtte automatikusan emlékeztetőt küld.",
        en: "Set a deposit at booking, or a cancellation rule (e.g. deducted from pass within 12 hours). AI sends a reminder 24 hours ahead automatically.",
      },
    },
    {
      question: {
        hu: "Hogy kezelem az érzékeny vendégadatokat?",
        en: "How do I handle sensitive client data?",
      },
      answer: {
        hu: "Minden vendég-karton titkosítva, EU-szervereken tárolva, GDPR-megfelelően. Egészségügyi információk csak neked (és általad jogosított masszőröknek) láthatóak.",
        en: "Every client card is encrypted and stored on EU servers, GDPR-compliant. Health info is visible only to you (and therapists you authorize).",
      },
    },
  ],

  monetization: [
    {
      icon: "Heart",
      title: { hu: "Klasszikus masszázsok", en: "Classic massages" },
      examples: [
        { hu: "60 perc svéd", en: "60-minute Swedish" },
        { hu: "Mélyszöveti masszázs", en: "Deep tissue" },
        { hu: "Aromaterápiás masszázs", en: "Aromatherapy massage" },
      ],
      priceHint: {
        hu: "Példa: 80 kezelés × 13 500 Ft = 1 080 000 Ft / hó (60 perc svéd 12:15e Ft)",
        en: "Example: 80 treatments × 13,500 HUF = 1,080,000 HUF / month (60-min Swedish 12:15k HUF)",
      },
    },
    {
      icon: "Activity",
      title: { hu: "Sport és terápiás", en: "Sports and therapeutic" },
      examples: [
        { hu: "Sportmasszázs", en: "Sports massage" },
        { hu: "Trigger-pont terápia", en: "Trigger point therapy" },
        { hu: "Rehabilitációs kezelés", en: "Rehab treatment" },
      ],
      priceHint: {
        hu: "Példa: 30 sport × 16 000 Ft = 480 000 Ft / hó (sportmasszázs 14:18e Ft)",
        en: "Example: 30 sports × 16,000 HUF = 480,000 HUF / month (sports 14:18k HUF)",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "Wellness csomagok", en: "Wellness packages" },
      examples: [
        { hu: "Páros relax csomag", en: "Couples relax package" },
        { hu: "Pre/postnatal sorozat", en: "Pre/postnatal series" },
        { hu: "Stresszoldó hétvége", en: "Stress-relief weekend" },
      ],
      priceHint: {
        hu: "Példa: 8 páros csomag × 28 000 Ft = 224 000 Ft / hó",
        en: "Example: 8 couples packages × 28,000 HUF = 224,000 HUF / month",
      },
    },
    {
      icon: "Package",
      title: { hu: "Termékek és kiegészítők", en: "Products and add-ons" },
      examples: [
        { hu: "Masszázs olajok", en: "Massage oils" },
        { hu: "Otthoni nyújtó eszközök", en: "Home stretch tools" },
        { hu: "Ajándékutalvány", en: "Gift voucher" },
      ],
      priceHint: {
        hu: "Passzív bevétel: ~80 000 Ft / hó",
        en: "Passive revenue: ~80,000 HUF / month",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Bérletek és tagságok", en: "Passes and memberships" },
      examples: [
        { hu: "5 alkalmas svéd", en: "5-session Swedish" },
        { hu: "10 alkalmas sport", en: "10-session sports" },
        { hu: "Havi VIP", en: "Monthly VIP" },
      ],
      priceHint: {
        hu: "Stúdió-szint: 3 masszőr × 25 bérleti vendég × 18 000 Ft = 1 350 000 Ft / hó potenciál (5 alkalmas 55:70e Ft)",
        en: "Studio level: 3 therapists × 25 pass holders × 18,000 HUF = 1,350,000 HUF / month potential (5-session 55:70k HUF)",
      },
    },
  ],

  results: [
    {
      value: "+52%",
      label: { hu: "5-alkalmas bérlet-eladás", en: "5-session pass sales" },
    },
    {
      value: "-65%",
      label: { hu: "lemondás 24h-n belül", en: "cancellations within 24h" },
    },
    {
      value: "4.9★",
      label: { hu: "vendég-elégedettség", en: "guest satisfaction" },
    },
    {
      value: "30 perc",
      label: {
        hu: "heti adminisztráció (régen: 12 óra)",
        en: "weekly admin (was: 12 hours)",
      },
    },
  ],
  testimonial: {
    quote: {
      hu: "A bérleteim eladása megduplázódott, mióta az AI észreveszi a visszatérő vendégeket és csomagot ajánl nekik. Én csak masszírozok.",
      en: "My pass sales doubled since AI started spotting return clients and suggesting packages. I just do the massage.",
    },
    name: "Szabó Gergő",
    role: {
      hu: "Masszőr, mobil szolgáltatás",
      en: "Massage therapist, mobile service",
    },
    city: { hu: "Pécs", en: "Pécs" },
    avatarInitials: "SG",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a kezeléseid",
        en: "Tell us your treatments in chat",
      },
      body: {
        hu: "Svéd, sport, mély szöveti, terhes: az AI strukturálja és árazza.",
        en: "Swedish, sports, deep tissue, prenatal: AI structures and prices them.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI elkészíti az oldalt és bérleteket",
        en: "AI builds the site and passes",
      },
      body: {
        hu: "5-alkalmas bérlet, ajándékutalvány, csomagok: élesítve.",
        en: "5-session pass, gift cards, packages: all live.",
      },
    },
    {
      step: 3,
      title: {
        hu: "Importáljuk a meglévő vendégeidet",
        en: "We import your existing clients",
      },
      body: {
        hu: "CSV-ből vagy a régi rendszerből, kartonokkal.",
        en: "From CSV or your old system, with full client cards.",
      },
    },
    {
      step: 4,
      title: {
        hu: "Élő, vendégek online foglalnak",
        en: "Live, clients book online",
      },
      body: {
        hu: "Helyszín-választás, előleg, automata emlékeztető.",
        en: "Location selection, deposit, auto-reminders.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Stripe",
    "SimplePay",
    "Apple Pay",
    "Google Pay",
    "WhatsApp Business",
    "Meta",
  ],
  breadcrumbLabel: {
    hu: "Masszőrök és masszázs-stúdiók",
    en: "Massage therapists & studios",
  },
  similarPersonas: [
    "physiotherapists",
    "beauticians-beauty-salons",
    "yoga-pilates-instructors",
  ],
};
